import { Button, Flex, Skeleton, Text, VStack } from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ToggleButton } from "src/components/Buttons/ToggleButton";
import { GenericError } from "src/components/Feedback/GenericError";
import {
  DateRangeInput,
  type DateRange,
} from "src/components/Inputs/DateRangeInput";
import {
  Item,
  SearchSelect,
} from "src/components/Inputs/SearchSelect/SearchSelect";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { ResizableDrawer } from "src/components/Layout/ResizableDrawer";
import { SearchAndFilterTypes } from "src/constants";
import * as RD from "src/types/remoteData";

interface PaymentActivityFiltersProps {
  isOpen: boolean;
  onToggleFilter: () => void;
  onClearFilters?: () => void;
  paymentTemplates: RD.RemoteData<unknown, Item[]>;
}

export const DEFAULT_PAYMENT_STATUSES = ["pending", "paid"] as const;

const STATUS_OPTIONS = [
  { id: "pending", label: "Pending" },
  { id: "paid", label: "Paid" },
  { id: "canceled", label: "Canceled" },
];

const getSelectedDateRange = (
  searchParams: URLSearchParams
): DateRange | null => {
  const daysAgo = searchParams.get(SearchAndFilterTypes.DaysAgo);
  const dateFrom = searchParams.get(SearchAndFilterTypes.DateFrom) ?? "";
  const dateTo = searchParams.get(SearchAndFilterTypes.DateTo) ?? "";

  switch (daysAgo) {
    case "1":
      return { key: "LastDay" };
    case "7":
      return { key: "Last7Days" };
    case "30":
      return { key: "Last30Days" };
    case null:
      break;
    default:
      return { key: "CustomLastDays", days: daysAgo };
  }

  if (!dateFrom && !dateTo) return null;

  return { key: "CustomDateRange", dateFrom, dateTo };
};

export const PaymentActivityFilters: React.FC<PaymentActivityFiltersProps> = ({
  isOpen,
  onToggleFilter,
  onClearFilters,
  paymentTemplates,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDirty, setIsDirty] = useState(false);
  const [localSearchParams, setLocalSearchParams] = useState<URLSearchParams>(
    new URLSearchParams(searchParams)
  );
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange | null>(
    () => getSelectedDateRange(searchParams)
  );

  useEffect(() => {
    setLocalSearchParams(new URLSearchParams(searchParams));
    setSelectedDateRange(getSelectedDateRange(searchParams));
    setIsDirty(false);
  }, [searchParams]);

  const selectedPaymentTemplateId = localSearchParams.get("paymentTemplateId");
  const selectedStatuses = useMemo(() => {
    const statusParam = localSearchParams.get("status");
    if (!statusParam) return [...DEFAULT_PAYMENT_STATUSES];
    return statusParam.split(",");
  }, [localSearchParams]);

  const handlePaymentTemplateChange = useCallback(
    (item: Item | null) => {
      const newParams = new URLSearchParams(localSearchParams);
      if (!item) {
        newParams.delete("paymentTemplateId");
      } else {
        newParams.set("paymentTemplateId", item.key);
      }
      newParams.delete(SearchAndFilterTypes.Offset);
      setLocalSearchParams(newParams);
      setIsDirty(true);
    },
    [localSearchParams]
  );

  const handleStatusToggle = useCallback(
    (statusId: string) => {
      const newStatuses = selectedStatuses.includes(statusId)
        ? selectedStatuses.filter((s) => s !== statusId)
        : [...selectedStatuses, statusId];

      const newParams = new URLSearchParams(localSearchParams);
      const isDefaultStatusFilter =
        newStatuses.length === DEFAULT_PAYMENT_STATUSES.length &&
        DEFAULT_PAYMENT_STATUSES.every((status) =>
          newStatuses.includes(status)
        );

      if (newStatuses.length === 0 || isDefaultStatusFilter) {
        newParams.delete("status");
      } else {
        newParams.set("status", newStatuses.join(","));
      }
      newParams.delete(SearchAndFilterTypes.Offset);
      setLocalSearchParams(newParams);
      setIsDirty(true);
    },
    [localSearchParams, selectedStatuses]
  );

  const handleDateRangeChange = useCallback(
    (dateRange: DateRange | null) => {
      setSelectedDateRange(dateRange);
      const newParams = new URLSearchParams(localSearchParams);
      newParams.delete(SearchAndFilterTypes.DaysAgo);
      newParams.delete(SearchAndFilterTypes.DateFrom);
      newParams.delete(SearchAndFilterTypes.DateTo);

      switch (dateRange?.key) {
        case "LastDay":
          newParams.set(SearchAndFilterTypes.DaysAgo, "1");
          break;
        case "Last7Days":
          newParams.set(SearchAndFilterTypes.DaysAgo, "7");
          break;
        case "Last30Days":
          newParams.set(SearchAndFilterTypes.DaysAgo, "30");
          break;
        case "CustomLastDays":
          if (Number(dateRange.days) > 0) {
            newParams.set(SearchAndFilterTypes.DaysAgo, dateRange.days);
          }
          break;
        case "CustomDateRange":
          if (dateRange.dateFrom) {
            newParams.set(SearchAndFilterTypes.DateFrom, dateRange.dateFrom);
          }
          if (dateRange.dateTo) {
            newParams.set(SearchAndFilterTypes.DateTo, dateRange.dateTo);
          }
          break;
      }
      newParams.delete(SearchAndFilterTypes.Offset);
      setLocalSearchParams(newParams);
      setIsDirty(true);
    },
    [localSearchParams]
  );

  const handleApplyFilters = useCallback(() => {
    setSearchParams(localSearchParams);
    setIsDirty(false);
    onToggleFilter();
  }, [localSearchParams, setSearchParams, onToggleFilter]);

  const handleClearAll = useCallback(() => {
    const newParams = new URLSearchParams();
    setSearchParams(newParams);
    setLocalSearchParams(newParams);
    setSelectedDateRange(null);
    setIsDirty(false);
    onClearFilters?.();
    onToggleFilter();
  }, [setSearchParams, onClearFilters, onToggleFilter]);

  const hasActiveFilters = useMemo(() => {
    return (
      searchParams.get("paymentTemplateId") ||
      searchParams.get("status") ||
      searchParams.get(SearchAndFilterTypes.DaysAgo) ||
      searchParams.get(SearchAndFilterTypes.DateFrom) ||
      searchParams.get(SearchAndFilterTypes.DateTo)
    );
  }, [searchParams]);

  const footerContent = (
    <>
      <Button
        onClick={handleClearAll}
        variant="outline"
        colorScheme="gray"
        flex="1"
        isDisabled={!hasActiveFilters && !isDirty}
      >
        Clear all
      </Button>
      <Button
        onClick={handleApplyFilters}
        variant="solid"
        colorScheme="primary"
        flex="1"
        isDisabled={!isDirty}
      >
        Apply
      </Button>
    </>
  );

  return (
    <ResizableDrawer
      autosaveId="paymentActivityFilterDrawerWidth"
      isOpen={isOpen}
      onClose={onToggleFilter}
      header="Filter"
      footer={footerContent}
    >
      <VStack align="stretch" spacing={6}>
        <VStack align="stretch" spacing={2}>
          <Text variant="filterLabel">Payment</Text>
          <RemoteDataView
            remoteData={paymentTemplates}
            error={() => <GenericError variant="inline" />}
            loading={<Skeleton height="2.5rem" />}
          >
            {(items) => (
              <SearchSelect
                label=""
                items={items}
                selectedItem={
                  items.find((t) => t.key === selectedPaymentTemplateId) ?? null
                }
                onChange={handlePaymentTemplateChange}
                placeholder="Type a payment name"
                variant="unlocked"
              />
            )}
          </RemoteDataView>
        </VStack>

        <VStack align="stretch" spacing={2}>
          <Text variant="filterLabel">Status</Text>
          <Flex flexWrap="wrap" gap={2}>
            {STATUS_OPTIONS.map((opt) => (
              <ToggleButton
                key={opt.id}
                active={selectedStatuses.includes(opt.id)}
                onClick={() => handleStatusToggle(opt.id)}
                display="block"
                overflow="hidden"
                textOverflow="ellipsis"
              >
                {opt.label}
              </ToggleButton>
            ))}
          </Flex>
        </VStack>

        <VStack align="stretch" spacing={2}>
          <DateRangeInput
            label="Date"
            selectedDateRange={selectedDateRange}
            onChange={handleDateRangeChange}
          />
        </VStack>
      </VStack>
    </ResizableDrawer>
  );
};
